import React, { useCallback, useMemo } from 'react'
import { Field, Formik } from 'formik'
import { makeStyles } from '@material-ui/core/styles'
import { isEqual } from 'lodash'
import yup from 'civic-champs-shared/utils/yup'
import { CkEditorStyledField } from 'civic-champs-shared/formik/components/CkEditorStyledField'
import { StyledInput } from 'civic-champs-shared/formik/components/StyledInput'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { FormModal, FormModalScreen } from 'components/FormModal'
import RadioGroupFormField from 'components/RadioGroupFormField'
import { OpenGroupPrompt } from 'group/components/OpenGroupPrompt'

const overviewSchema = yup.object({
  name: yup.string().trim().required(),
  description: yup.string().trim().nullable().notRequired(),
  closed: yup.boolean().required(),
  useApplicationLink: yup.boolean().required(),
})

export interface GroupOverviewFormValues {
  name: string
  description: string
  closed: boolean
  useApplicationLink: boolean
}

export interface EditGroupOverviewDialogProps {
  showing: boolean
  close: () => void
  complete: (value: boolean) => void
  group: {
    name: string
    description?: string | null
    closed: boolean
    useApplicationLink: boolean
  }
  onSubmit: (payload: Partial<GroupOverviewFormValues>) => Promise<unknown>
  canChangeMembership: boolean
  canChangeStatus: boolean
  nameReadOnly?: boolean
}

const useStyles = makeStyles({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    width: '100%',
    boxSizing: 'border-box',
  },
  choiceSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  sectionTitle: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    margin: 0,
  },
  radioIndent: {
    boxSizing: 'border-box',
    width: '100%',
    paddingLeft: '20px',
    '& .MuiFormGroup-root': {
      gap: '10px',
    },
    '& .MuiFormControlLabel-root': {
      margin: 0,
      alignItems: 'center',
      minHeight: '20px',
    },
    '& .MuiRadio-root': {
      padding: 0,
    },
    '& .MuiIconButton-root': {
      padding: 0,
    },
    '& .MuiFormControlLabel-label': {
      marginLeft: '13px',
      lineHeight: '20px',
    },
  },
  footerCancel: {
    padding: '8px 12px',
    fontFamily: 'Nunito',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: '#0F5DB5',
  },
  footerSave: {
    padding: '8px 24px',
    borderRadius: '100px',
    fontFamily: 'Nunito',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    backgroundColor: '#3776CF',
    '&:hover': {
      backgroundColor: '#2f68b8',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
    },
  },
})

const buildInitialValues = (group: EditGroupOverviewDialogProps['group']): GroupOverviewFormValues => ({
  name: group.name || '',
  description: group.description || '',
  closed: group.closed,
  useApplicationLink: group.useApplicationLink,
})

export default function EditGroupOverviewDialog({
  showing,
  close,
  complete,
  group,
  onSubmit,
  canChangeMembership,
  canChangeStatus,
  nameReadOnly = false,
}: EditGroupOverviewDialogProps) {
  const classes = useStyles()
  const showOpenGroupPrompt = useShowPrompt(OpenGroupPrompt)
  const initialValues = useMemo(() => buildInitialValues(group), [group])

  const handleSubmit = useCallback(
    async (values: GroupOverviewFormValues) => {
      const payload = overviewSchema.cast(values) as GroupOverviewFormValues
      const switchedToOpen = group.closed && !payload.closed

      if (switchedToOpen && canChangeMembership) {
        const confirmed = await showOpenGroupPrompt({
          onSubmit: async () => undefined,
          loading: false,
          group,
        })
        if (!confirmed) {
          return
        }
      }

      if (nameReadOnly) {
        const { name: _name, ...payloadWithoutName } = payload
        await onSubmit(payloadWithoutName)
      } else {
        await onSubmit(payload)
      }
      complete(true)
    },
    [canChangeMembership, complete, group, nameReadOnly, onSubmit, showOpenGroupPrompt],
  )

  return (
    <Formik initialValues={initialValues} validationSchema={overviewSchema} onSubmit={handleSubmit} enableReinitialize>
      {({ submitForm, isSubmitting, values, isValid }) => {
        const isDirty = !isEqual(values, initialValues)

        return (
          <FormModal showing={showing} close={close} id="edit-group-overview-dialog" title="Edit Group Overview">
            <FormModalScreen
              onBack={close}
              backText="Cancel"
              onNext={submitForm}
              nextText="Save"
              nextDisabled={!isDirty || !isValid}
              disabled={isSubmitting}
              nextIsLoading={isSubmitting}
              backClassName={classes.footerCancel}
              nextClassName={classes.footerSave}
            >
              <div className={classes.modalContent}>
                <Field
                  name="name"
                  component={StyledInput}
                  label={nameReadOnly ? 'Group Name' : 'Group Name*'}
                  placeholder="Write the group's name here..."
                  disabled={nameReadOnly || isSubmitting}
                />
                <Field
                  name="description"
                  label="Group Description"
                  placeholder="Describe the group here...."
                  component={CkEditorStyledField}
                  id="edit-group-overview-description"
                  _dangerouslyRemoveDialogTabindex
                  marginless
                  smaller
                  multiline
                  rows={8}
                />
                {canChangeMembership && (
                  <div className={classes.choiceSection}>
                    <span className={classes.sectionTitle}>Group Membership:</span>
                    <div className={classes.radioIndent}>
                      <RadioGroupFormField
                        name="closed"
                        valueIsBoolean
                        disabled={isSubmitting}
                        options={[
                          { label: 'Requires Approval', value: true },
                          { label: 'Does Not Require Approval', value: false },
                        ]}
                      />
                    </div>
                  </div>
                )}
                {canChangeStatus && (
                  <div className={classes.choiceSection}>
                    <span className={classes.sectionTitle}>Status:</span>
                    <div className={classes.radioIndent}>
                      <RadioGroupFormField
                        name="useApplicationLink"
                        valueIsBoolean
                        disabled={isSubmitting}
                        options={[
                          { label: 'Accepting Applications', value: true },
                          { label: 'Not Accepting Applications', value: false },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>
            </FormModalScreen>
          </FormModal>
        )
      }}
    </Formik>
  )
}
